import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { MessageSquare, Send, User, Feather } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "अभी";
  if (diff < 3600) return `${Math.floor(diff / 60)} मिनट पहले`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} घंटे पहले`;
  return d.toLocaleDateString("hi-IN");
}

export default function CommentsSection({ slug }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", content: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/news/${slug}/comments`)
      .then((r) => setComments(r.data))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (slug) load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.content.trim()) {
      toast.error("नाम और टिप्पणी दोनों आवश्यक हैं");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/comments", {
        news_slug: slug,
        name: form.name.trim(),
        email: form.email.trim() || null,
        content: form.content.trim(),
      });
      toast.success("धन्यवाद! आपकी टिप्पणी अनुमोदन के बाद प्रकाशित होगी।");
      setForm({ name: "", email: "", content: "" });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-12 pt-8 border-t border-slate-200" data-testid="comments-section">
      <div className="flex items-center gap-3 mb-6">
        <MessageSquare className="w-5 h-5 text-[#EA580C]"/>
        <h3 className="font-devnagri-heading text-2xl font-black text-[#0F172A]">
          पाठकों की प्रतिक्रिया <span className="text-slate-400 text-lg font-normal">({comments.length})</span>
        </h3>
      </div>

      {/* Form */}
      <form onSubmit={submit} className="bg-slate-50 border border-slate-200 rounded-lg p-5 mb-8" data-testid="comment-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-admin text-slate-600 mb-1 block">आपका नाम *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="जैसे: राहुल शर्मा" maxLength={80} required data-testid="comment-name"/>
          </div>
          <div>
            <label className="text-xs font-admin text-slate-600 mb-1 block">ईमेल (गुप्त रहेगा)</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="optional@example.com" data-testid="comment-email"/>
          </div>
        </div>
        <div className="mb-3">
          <label className="text-xs font-admin text-slate-600 mb-1 block">आपकी प्रतिक्रिया *</label>
          <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
            rows={4} maxLength={2000} placeholder="इस समाचार पर अपने विचार साझा करें..." required
            data-testid="comment-content"/>
          <div className="text-right text-[10px] text-slate-400 mt-1">{form.content.length}/2000</div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] text-slate-500">✧ आपकी टिप्पणी संपादक की समीक्षा के बाद प्रकाशित की जायेगी।</p>
          <Button type="submit" disabled={submitting}
            className="bg-[#0F172A] hover:bg-[#1E3A8A] text-white" data-testid="comment-submit">
            <Send className="w-4 h-4 mr-2"/> {submitting ? "भेज रहे हैं…" : "टिप्पणी भेजें"}
          </Button>
        </div>
      </form>

      {/* List */}
      {loading ? (
        <div className="text-slate-500 text-sm">लोड हो रहा है…</div>
      ) : comments.length === 0 ? (
        <div className="text-slate-500 text-sm italic bg-white border border-dashed border-slate-200 rounded-lg p-6 text-center">
          अभी कोई प्रतिक्रिया नहीं। सबसे पहले टिप्पणी करें!
        </div>
      ) : (
        <ul className="space-y-4">
          {comments.map((c) => (
            <li key={c.id} className="space-y-2" data-testid={`comment-${c.id}`}>
              <div className="flex gap-3 bg-white border border-slate-100 rounded-lg p-4">
                <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white flex items-center justify-center font-bold">
                  {c.name?.[0]?.toUpperCase() || <User className="w-4 h-4"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-bold text-[#0F172A] text-sm">{c.name}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">{relTime(c.created_at)}</div>
                  </div>
                  <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{c.content}</div>
                </div>
              </div>
              {c.editor_reply && (
                <div className="ml-8 sm:ml-12 relative" data-testid={`editor-reply-${c.id}`}>
                  <div className="absolute -left-4 top-4 w-4 h-px bg-amber-400/50"></div>
                  <div className="flex gap-3 bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white border-l-4 border-amber-400 rounded-lg p-4 shadow-md">
                    <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-[#0F172A] flex items-center justify-center shadow ring-2 ring-amber-500/30">
                      <Feather className="w-4 h-4" strokeWidth={2.5}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <div className="font-black text-amber-300 tracking-wider text-sm" style={{ fontFamily: "'Times New Roman', serif", fontStyle: "italic" }}>
                          JAKIR MOMIN
                        </div>
                        <span className="text-[9px] uppercase tracking-widest bg-amber-400 text-[#0F172A] px-1.5 py-0.5 rounded font-bold">प्रधान संपादक</span>
                        <span className="text-[10px] text-slate-400">{relTime(c.editor_replied_at)}</span>
                      </div>
                      <div className="text-slate-100 text-sm leading-relaxed whitespace-pre-wrap italic">{c.editor_reply}</div>
                    </div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
